/**
 * React hook for JWT authentication with wallet signing.
 * 
 * Handles the full authentication flow:
 * 1. Request challenge from backend
 * 2. Sign challenge with wallet
 * 3. Verify and receive JWT token
 * 4. Store token and manage refresh
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useQueryClient } from "@tanstack/react-query";
import mixpanel from "mixpanel-browser";
import {
  getAuthChallenge,
  verifyAuth,
  storeToken,
  getStoredToken,
  getStoredWallet,
  getStoredUserId,
  getStoredEmail,
  getStoredLoginMethod,
  clearStoredToken,
  tokenNeedsRefresh,
  getTokenExpiry,
} from "../auth";
import { wsClient } from "../ws-client";
import { debug } from "@/lib/debug";
import { useAccountStore } from "../store/account-store";

interface UseAuthTokenReturn {
  /** JWT token if authenticated */
  token: string | null;
  /** Whether authentication is in progress */
  isAuthenticating: boolean;
  /** Authentication error if any */
  error: Error | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** User ID (database UUID from backend) */
  userId: string | null;
  /** Wallet address (for display and signing) */
  walletAddress: string | null;
  /** User's linked email (for wallet users who linked email) */
  email: string | null;
  /** Login method: 'wallet', 'privy', or 'both' */
  loginMethod: string | null;
  /** Whether this is a brand new user (first signup) - for Meta Pixel Lead tracking */
  isNewUser: boolean;
  /** Token expiry timestamp */
  expiresAt: number | null;
  /** Authenticate with wallet */
  authenticate: () => Promise<void>;
  /** Clear authentication */
  logout: () => void;
}

/**
 * Hook for managing JWT authentication with wallet signing.
 */
export function useAuthToken(): UseAuthTokenReturn {
  const { publicKey, signMessage, connected } = useWallet();
  const queryClient = useQueryClient();
  
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [userId, setUserId] = useState<string | null>(() => getStoredUserId());
  const [email, setEmail] = useState<string | null>(() => getStoredEmail());
  const [loginMethod, setLoginMethod] = useState<string | null>(() => getStoredLoginMethod());
  const [isNewUser, setIsNewUser] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(() => getTokenExpiry());

  // Wallet address for display and signing
  const walletAddress = publicKey?.toBase58() ?? null;

  // Update userId, email, and loginMethod from stored values when token is present
  useEffect(() => {
    if (token && !userId) {
      const storedUserId = getStoredUserId();
      if (storedUserId) {
        setUserId(storedUserId);
      }
    } else if (!token) {
      setUserId(null);
    }
    
    // Also sync email state
    if (token && !email) {
      const storedEmail = getStoredEmail();
      if (storedEmail) {
        setEmail(storedEmail);
      }
    } else if (!token) {
      setEmail(null);
    }
    
    // Also sync loginMethod state
    if (token && !loginMethod) {
      const storedLoginMethod = getStoredLoginMethod();
      if (storedLoginMethod) {
        setLoginMethod(storedLoginMethod);
      }
    } else if (!token) {
      setLoginMethod(null);
    }
  }, [token, userId, email, loginMethod]);

  // Update WebSocket client token
  useEffect(() => {
    wsClient.setToken(token);
  }, [token]);

  // Check for stored token on mount
  // IMPORTANT: If we have a token but no stored userId, this is an old session
  // that needs to be migrated. Clear it so user re-authenticates and gets UUID.
  useEffect(() => {
    const stored = getStoredToken();
    debug.log('Auth', `Checking stored token on mount: hasToken=${!!stored}`);
    
    if (stored) {
      const storedUserId = getStoredUserId();
      const storedWallet = getStoredWallet();
      debug.log('Auth', `Stored session: userId=${storedUserId?.slice(0,8)}, wallet=${storedWallet?.slice(0,8)}`);
      
      if (storedUserId) {
        // Valid new session with UUID
        debug.log('Auth', 'Restoring valid session with UUID');
        setToken(stored);
        setExpiresAt(getTokenExpiry());
        setUserId(storedUserId);
      } else {
        // Old session without UUID - clear it to force re-authentication
        debug.warn('Auth', 'Old session detected (no userId stored). Clearing to trigger re-auth.');
        clearStoredToken();
        setToken(null);
        setUserId(null);
        setExpiresAt(null);
      }
    } else {
      debug.log('Auth', 'No stored token found');
    }
  }, []);

  // Track the last known wallet address to detect wallet changes
  const lastWalletRef = useRef<string | null>(null);
  
  // Handle wallet changes and disconnects properly
  // This avoids clearing the token during page refresh when wallet is just reconnecting
  useEffect(() => {
    const currentWallet = publicKey?.toBase58() || null;
    const storedWallet = getStoredWallet();
    
    debug.log('Auth', `Wallet state changed: connected=${connected}, currentWallet=${currentWallet?.slice(0,8)}, storedWallet=${storedWallet?.slice(0,8)}`);
    
    // Case 1: Wallet connected
    if (connected && currentWallet) {
      // Check if this is a different wallet than the stored token belongs to
      if (storedWallet && storedWallet !== currentWallet) {
        // User switched wallets - clear the old token
        debug.log('Auth', 'Different wallet detected, clearing token');
        setToken(null);
        setUserId(null);
        setEmail(null);
        setLoginMethod(null);
        setExpiresAt(null);
        clearStoredToken();
        wsClient.setToken(null);
      } else {
        debug.log('Auth', 'Wallet connected (same as stored)');
      }
      lastWalletRef.current = currentWallet;
    }
    
    // Case 2: Wallet explicitly disconnected (not just page refresh)
    // We know it's an explicit disconnect if we had a wallet before and now it's gone
    // AND we're not in the initial loading phase (lastWalletRef has been set)
    if (!connected && lastWalletRef.current !== null) {
      debug.log('Auth', 'Wallet disconnected, clearing token');
      setToken(null);
      setUserId(null);
      setEmail(null);
      setLoginMethod(null);
      setExpiresAt(null);
      clearStoredToken();
      wsClient.setToken(null);
      lastWalletRef.current = null;
    }
  }, [connected, publicKey]);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!token || !expiresAt) return;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiresAt - now;
    
    // Refresh 1 hour before expiry
    const refreshIn = Math.max(0, (timeUntilExpiry - 3600) * 1000);
    
    if (refreshIn > 0 && refreshIn < 24 * 60 * 60 * 1000) {
      const timeout = setTimeout(() => {
        if (connected && publicKey && signMessage) {
          // Attempt silent refresh
          authenticateInternal().catch(console.error);
        }
      }, refreshIn);

      return () => clearTimeout(timeout);
    }
  }, [token, expiresAt, connected, publicKey, signMessage]);

  const authenticateInternal = useCallback(async () => {
    if (!publicKey || !signMessage) {
      throw new Error("Wallet not connected or does not support signing");
    }

    const wallet = publicKey.toBase58();

    // Step 1: Get challenge
    const challenge = await getAuthChallenge(wallet);

    // Step 2: Sign the message
    const encoder = new TextEncoder();
    const messageBytes = encoder.encode(challenge.message);
    const signature = await signMessage(messageBytes);

    // Convert signature to base58
    const signatureB58 = encodeBase58(signature);

    // Step 3: Verify and get token
    const response = await verifyAuth({
      wallet,
      signature: signatureB58,
      nonce: challenge.nonce,
    });

    // Store token with wallet address, user ID (UUID from backend), email, and login method
    storeToken(response.token, response.expires_at, wallet, response.user_id, response.email, response.login_method);
    setToken(response.token);
    setExpiresAt(response.expires_at);
    setUserId(response.user_id);  // Now this is the database UUID
    setEmail(response.email || null);  // Set linked email if present
    setLoginMethod(response.login_method || null);  // Set login method
    setIsNewUser(response.is_new_user ?? false);  // Track if this is a new signup

    // Track user signup/login in Mixpanel
    try {
      mixpanel.identify(response.user_id);
      mixpanel.people.set({
        wallet_address: wallet,
        login_method: 'wallet',
      });
      mixpanel.track('user_signup', {
        wallet_address: wallet,
        login_method: 'wallet',
        user_id: response.user_id,
      });
    } catch (err) {
      // Don't fail authentication if Mixpanel tracking fails
      debug.error('Auth', 'Mixpanel tracking error:', err);
    }

    return response;
  }, [publicKey, signMessage]);

  const authenticate = useCallback(async () => {
    if (isAuthenticating) return;

    setIsAuthenticating(true);
    setError(null);

    try {
      await authenticateInternal();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, [authenticateInternal, isAuthenticating]);

  const logout = useCallback(() => {
    setToken(null);
    setUserId(null);
    setEmail(null);
    setLoginMethod(null);
    setIsNewUser(false);
    setExpiresAt(null);
    setError(null);
    clearStoredToken();
    wsClient.setToken(null);
    
    // Invalidate AND remove all user-specific queries
    // Using removeQueries ensures they're fully cleared (not just stale)
    // This is critical because account-sync has staleTime: Infinity
    queryClient.removeQueries({ queryKey: ["account-sync"] });
    queryClient.removeQueries({ queryKey: ["backend-account"] });
    queryClient.removeQueries({ queryKey: ["backend-positions"] });
    queryClient.removeQueries({ queryKey: ["backend-orders"] });
    // Clear notification queries
    queryClient.removeQueries({ queryKey: ["user-notifications"] });
    queryClient.removeQueries({ queryKey: ["notification-preferences"] });
    
    // Clear Zustand account store - critical for clearing old user's data
    useAccountStore.getState().reset();
  }, [queryClient]);

  return {
    token,
    isAuthenticating,
    error,
    isAuthenticated: !!token,
    userId,
    walletAddress,
    email,
    loginMethod,
    isNewUser,
    expiresAt,
    authenticate,
    logout,
  };
}

/**
 * Encode bytes to base58 (Solana uses base58 for signatures).
 */
function encodeBase58(bytes: Uint8Array): string {
  const ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
  
  let result = "";
  let value = BigInt(0);
  
  for (const byte of bytes) {
    value = value * BigInt(256) + BigInt(byte);
  }
  
  while (value > 0) {
    const remainder = Number(value % BigInt(58));
    result = ALPHABET[remainder] + result;
    value = value / BigInt(58);
  }
  
  // Handle leading zeros
  for (const byte of bytes) {
    if (byte === 0) {
      result = "1" + result;
    } else {
      break;
    }
  }
  
  return result || "1";
}

/**
 * Hook to check if authentication is needed.
 * Returns true if wallet is connected but not authenticated.
 */
export function useNeedsAuth(): boolean {
  const { connected } = useWallet();
  const token = getStoredToken();
  
  return connected && !token;
}

/**
 * Hook to get current auth status.
 */
export function useAuthStatus(): {
  isConnected: boolean;
  isAuthenticated: boolean;
  needsRefresh: boolean;
} {
  const { connected } = useWallet();
  const token = getStoredToken();
  
  return {
    isConnected: connected,
    isAuthenticated: !!token,
    needsRefresh: tokenNeedsRefresh(),
  };
}
